import { Link } from 'react-router';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useSEO, mergeKeywordSets, COMPETITIVE_KEYWORD_CLUSTERS } from '../lib/seo';
import { StockHeroStrip, StockImage, WarmMarketingBackdrop } from '../components/marketing/MarketingVisuals';

export default function RelationshipCounselingApp() {
  useSEO({
    title: 'Relationship Counseling App: What to Look For (US)',
    description:
      'How to evaluate a relationship counseling app: privacy, scope, communication support, and when to seek a licensed therapist.',
    keywords: mergeKeywordSets(
      COMPETITIVE_KEYWORD_CLUSTERS.core as unknown as string[],
      COMPETITIVE_KEYWORD_CLUSTERS.communication as unknown as string[],
      ['relationship counseling app', 'couples counseling app', 'relationship help app'],
    ),
    canonical: '/relationship-counseling-app',
    ogType: 'website',
    twitterCard: 'summary_large_image',
  });

  return (
    <WarmMarketingBackdrop>
      <div className="mx-auto max-w-5xl space-y-8 px-4 py-10 md:py-14">
        <header className="grid gap-6 overflow-hidden rounded-3xl border border-violet-200/60 bg-white/90 shadow-xl md:grid-cols-[1.05fr_0.95fr] md:items-center">
          <div className="p-6 md:p-10">
            <h1 className="text-3xl font-black tracking-tight text-neutral-900 md:text-4xl">
              Relationship counseling app: choose the right scope
            </h1>
            <p className="mt-3 max-w-2xl text-neutral-600 md:text-lg">
              Apps can support skills practice and daily habits. They are not a substitute for emergency care or licensed
              therapy when you need it.
            </p>
          </div>
          <StockImage
            stockKey="supportCalm"
            width={800}
            height={560}
            priority
            alt="Person in a calm, professional setting — illustrative stock photo"
            className="h-52 w-full object-cover object-[center_15%] md:h-full md:min-h-[260px] md:rounded-none md:rounded-r-3xl"
          />
        </header>

        <StockHeroStrip stockKey="handsWalking" alt="Two people walking together — supportive connection, illustrative" />

        <Card id="methodology" className="border-violet-200/80 bg-white/95 p-6 shadow-md md:p-8">
          <h2 className="text-lg font-semibold text-neutral-900">How we think about counseling support</h2>
          <p className="mt-2 text-sm leading-relaxed text-neutral-700">
            TENSION focuses on measurable tension signals, communication coaching, and repair-oriented workflows. We
            describe capabilities honestly and point you to professional help for crises or clinical needs.
          </p>
        </Card>

        <div className="grid gap-4 md:grid-cols-2" id="criteria">
          <Card className="border-violet-100/90 bg-white/95 p-6 shadow-md">
            <h2 className="text-lg font-semibold text-neutral-900">Look for</h2>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-neutral-700">
              <li>Clear privacy and data boundaries.</li>
              <li>Practical communication and de-escalation tools.</li>
              <li>Transparency about what the app is not (not emergency services).</li>
            </ul>
          </Card>
          <Card className="border-violet-100/90 bg-white/95 p-6 shadow-md">
            <h2 className="text-lg font-semibold text-neutral-900">Watch out for</h2>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-neutral-700">
              <li>Vague claims without explaining how the product helps day to day.</li>
              <li>Pressure to replace therapy when you need licensed care.</li>
            </ul>
          </Card>
        </div>

        <Card className="border-violet-200/80 bg-gradient-to-r from-violet-50/95 to-fuchsia-50/90 p-8 shadow-lg">
          <h2 className="text-xl font-semibold text-neutral-900">Go deeper</h2>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link to="/blog/relationship-counseling-app-what-to-look-for">
              <Button variant="outline" className="border-violet-300 bg-white/90">
                Read the full guide
              </Button>
            </Link>
            <Link to="/guides">
              <Button variant="outline" className="border-violet-300 bg-white/90">
                TENSION guides
              </Button>
            </Link>
            <Link to="/pricing">
              <Button className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white shadow-md">View pricing</Button>
            </Link>
          </div>
        </Card>
      </div>
    </WarmMarketingBackdrop>
  );
}
